import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatChipsModule } from '@angular/material/chips';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule, MatNativeDateModule, MatRipple } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatTabsModule } from '@angular/material/tabs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatStepperModule } from '@angular/material/stepper';
import { MatToolbarModule } from '@angular/material/toolbar';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from './services/config.service';
import { ResourceMasterDataService } from './services/resource-master-data.service';
import { Log } from './services/logger.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CourseListComponent } from './components/entities/course/course-list/course-list.component';
import { CustomerCreateEditComponent } from './components/entities/customer-create-edit/customer-create-edit.component';
import { LoginComponent } from './components/home/login/login.component';
import { NotificationsComponent } from './components/home/notifications/notifications.component';
import { ProfileComponent } from './components/profile/profile.component';
import { ResourceCardsComponent } from './components/resources/resource-cards/resource-cards.component';
import { RequisitionCreateComponent, VirtualMachineComponent } from './components/resources/virtual-machine/virtual-machine.component';
import { CloudProviderPluginComponent } from './components/resources/wizard-plugins/cloud-provider-plugin/cloud-provider-plugin.component';
import { CloudProviderPreviewComponent } from './components/resources/wizard-plugins/cloud-provider-plugin/cloud-provider-preview/cloud-provider-preview.component';
import { SoftwareConfigPluginComponent } from './components/resources/wizard-plugins/software-config-plugin/software-config-plugin.component';
import { SoftwarePreviewComponent } from './components/resources/wizard-plugins/software-config-plugin/software-preview/software-preview.component';
import { MainLayoutComponent } from './components/shared/main-layout/main-layout.component';
import { NavHeaderComponent } from './components/shared/nav-header/nav-header.component';
import { SidebarComponent } from './components/shared/sidebar/sidebar.component';
import { NewCustomerDrawerComponent } from './components/shared/new-customer-drawer/new-customer-drawer.component';
import { SupportComponent } from './components/support/support.component';
import { WizardBuilderComponent } from './components/wizard-builder/wizard-builder.component';
import { DecoratorFactory, DYNAMIC_COMPONENTS_RETENTION } from './decoration-base/decorator-factory';
import { DecoratorModule } from './decoration-base/decorator.module';
import { EntityServicesModule } from './modules/entity-services.module';

import { CourseDetailComponent } from './components/entities/course/course-detail/course-detail.component';
import { TrainerCreateEditComponent } from './components/entities/trainer-create-edit/trainer-create-edit.component';
import { RequisitionComponent } from './components/entities/requisition/requisition.component';
import { RequisitionListComponent } from './components/entities/requisition-list/requisition-list.component';
import { ClassDetailV2Component } from './components/entities/class/class-detail-v2/class-detail-v2.component';
import { DetailTabComponent } from './components/entities/class/class-detail-v2/tabs/detail-tab/detail-tab.component';
import { ActivityTabComponent } from './components/entities/class/class-detail-v2/tabs/activity-tab/activity-tab.component';
import { ParticipantDetailsTabComponent } from './components/entities/class/class-detail-v2/tabs/participant-details-tab/participant-details-tab.component';
import { SettingsTabComponent } from './components/entities/class/class-detail-v2/tabs/settings-tab/settings-tab.component';
import { TenantSettingsComponent } from './components/entities/class/class-detail-v2/tabs/settings-tab/tenant-settings.component';
import { VmSubscriptionSettingsComponent } from './components/entities/class/class-detail-v2/tabs/settings-tab/vm-subscription-settings.component';
import { VmSubscriptionAddDialogComponent } from './components/entities/class/class-detail-v2/tabs/settings-tab/vm-subscription-add-dialog.component';
import { AccountSubscriptionSettingsComponent } from './components/entities/class/class-detail-v2/tabs/settings-tab/account-subscription-settings.component';
import { AccTemplateSettingsComponent } from './components/entities/class/class-detail-v2/tabs/settings-tab/acc-template-settings.component';
import { ScheduleSettingsComponent } from './components/entities/class/class-detail-v2/tabs/settings-tab/schedule-settings.component';
import { TimePickerComponent } from './components/entities/class/class-detail-v2/tabs/settings-tab/time-picker.component';
import { FacultySettingsComponent } from './components/entities/class/class-detail-v2/tabs/settings-tab/faculty-settings.component';
import { ParticipantsSettingsComponent } from './components/entities/class/class-detail-v2/tabs/settings-tab/participants-settings.component';
import { ForwardSettingsComponent } from './components/entities/class/class-detail-v2/tabs/settings-tab/forward-settings.component';
import { VmTemplateSettingsComponent } from './components/entities/class/class-detail-v2/tabs/settings-tab/vm-template-settings.component';
import { ViewVmConnectionTemplateDetailsComponent } from './components/entities/vm-connection-template/view-vm-connection-template-details/view-vm-connection-template-details.component';
import { VmConnectionTemplateCreatePanelComponent } from './components/entities/vm-connection-template/vm-connection-template-create-panel/vm-connection-template-create-panel.component';
import { VmConnectionTemplateAttachComponent } from './components/entities/vm-connection-template/vm-connection-template-attach/vm-connection-template-attach.component';
import { VmConnectionTemplateListComponent } from './components/entities/vm-connection-template/vm-connection-template-list/vm-connection-template-list.component';
import { TemplateInfoCardComponent } from './components/shared/template-info-card/template-info-card.component';
import { PersistPageSizeDirective } from './directives/persist-page-size.directive';
import { AdhocParticipantsDialogComponent } from './components/entities/class/class-detail-v2/tabs/settings-tab/adhoc-participants-dialog.component';
import { CustomerInvoiceComponent } from './components/entities/customer-invoice/customer-invoice.component';
import { DashboardComponent } from './components/shared/dashboard/dashboard.component';
import { ClassSessionComponent } from './components/entities/class-session/class-session.component';
import { TemplateDetailsComponent } from './components/resources/wizard-plugins/template-details/template-details.component';
import { TemplateDetailsPreviewComponent } from './components/resources/wizard-plugins/template-details/template-details-preview/template-details-preview.component';
import { TrimmerPipe } from './pipes/trimmer.pipe';
import { RequisitionPluginComponent } from './components/entities/requisition/plugins/requisition-plugin/requisition-plugin.component';
import { RequisitionPreviewComponent } from './components/entities/requisition/plugins/requisition-plugin/requisition-preview/requisition-preview.component';
import { TrainingSchedulesPluginComponent } from './components/entities/requisition/plugins/training-schedules-plugin/training-schedules-plugin.component';
import { TrainingSchedulesPreviewComponent } from './components/entities/requisition/plugins/training-schedules-plugin/training-schedules-preview/training-schedules-preview.component';
import { AdditionalInformationPluginComponent } from './components/entities/requisition/plugins/additional-information-plugin/additional-information-plugin.component';
import { AdditionalInformationPreviewComponent } from './components/entities/requisition/plugins/additional-information-plugin/additional-information-preview/additional-information-preview.component';

import { BillingDashboardComponent } from './components/entities/billing-dashboard/billing-dashboard.component';
import { TrainerRequisitionViewComponent } from './components/entities/requisition/trainer-requisition-view/trainer-requisition-view.component';
import { RequisitionPreviewViewComponent } from './components/entities/requisition/requisition-preview-view/requisition-preview-view.component';
import { SignUpComponent } from './components/home/sign-up/sign-up.component';
import { ForgotPasswordComponent } from './components/home/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './components/home/reset-password/reset-password.component';
import { CustomerEnquiryFormComponent } from './components/home/customer-enquiry/customer-enquiry-form/customer-enquiry-form.component';
import { CustomerEnquiryListComponent } from './components/home/customer-enquiry/customer-enquiry-list/customer-enquiry-list.component';
import { UserAccountPluginComponent } from './components/resources/wizard-plugins/user-account-plugin/user-account-plugin.component';
import { UserAccountComponent } from './components/resources/user-account/user-account.component';
import { ClassResourcesComponent } from './components/class-resources/class-resources.component';
import { NoAccessComponent } from './components/shared/no-access/no-access.component';

// Authentication components, services, and guards
import { UnauthorizedComponent } from './components/shared/unauthorized/unauthorized.component';
import { AuthService } from './services/auth.service';
import { AuthGuard } from './guards/auth.guard';

import { LoginGuard } from './guards/login.guard';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { MyRequisitionComponent } from './components/entities/requisition/my-requisition/my-requisition.component';
import { BusinessProcessComponent } from './components/settings/business-process/business-process.component';
import { UserCreationComponent } from './components/settings/user-creation/user-creation.component';
import { TeamsComponent } from './components/settings/teams/teams.component';
import { ForwardRequisitionComponent } from './components/home/forward-requisition/forward-requisition.component';
import { DeletionPolicyComponent } from './components/entities/policy/deletion-policy/deletion-policy.component';
import { PolicyTemplateListComponent } from './components/entities/policy/policy-template-list/policy-template-list.component';
import { ViewPolicyDetailsComponent } from './components/entities/policy/view-policy-details/view-policy-details.component';
import { VMTemplateSubscriptionsComponent } from './components/vm-template-subscriptions/vm-template-subscriptions.component';
import { TemplateSubBulkAddDialogComponent } from './components/vm-template-subscriptions/template-sub-bulk-add-dialog.component';
import { DeleteConfirmDialogComponent } from './components/vm-template-subscriptions/delete-confirm-dialog.component';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ResourcePolicyLinkingComponent } from './components/entities/policy/resource-policy-linking/resource-policy-linking.component';
import { PolicyTemplateLinkingComponent } from './components/entities/policy/policy-template-linking/policy-template-linking.component';
import { VmConnectionComponent } from './components/test/vm-connection/vm-connection.component';
import { VmAadStatusCardComponent } from './components/test/vm-connection/vm-aad-status-card/vm-aad-status-card.component';
import { RequisitionEditComponent } from './components/entities/requisition/requisition-edit/requisition-edit.component';
import { RequisitionCreateWizardComponent } from './components/entities/requisition/create-wizard/requisition-create-wizard.component';
import { ResourceCreatorV2Component } from './components/resource-creator-v2/resource-creator-v2.component';
import { UserAccountCreateWizardComponent } from './components/resources/create-wizard/user-account/user-account-create-wizard.component';
import { VmCreateWizardComponent } from './components/resources/create-wizard/vm/vm-create-wizard.component';
import { TemplateDetailsStepComponent } from './components/resources/create-wizard/shared/template-details-step/template-details-step.component';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { ConfirmDialogComponent } from './components/shared/confirm-dialog/confirm-dialog.component';
import { TypedConfirmDialogComponent } from './components/shared/typed-confirm-dialog/typed-confirm-dialog.component';
import { ResourcesTabComponent } from './components/entities/class/class-detail-v2/tabs/resources-tab/resources-tab.component';
import { UserVmsSubTabComponent } from './components/entities/class/class-detail-v2/tabs/resources-tab/sub-tabs/user-vms/user-vms-sub-tab.component';
import { TemplatesSubTabComponent } from './components/entities/class/class-detail-v2/tabs/resources-tab/sub-tabs/templates/templates-sub-tab.component';
import { UserAccountsSubTabComponent } from './components/entities/class/class-detail-v2/tabs/resources-tab/sub-tabs/user-accounts/user-accounts-sub-tab.component';
import { CostAnalysisSubTabComponent } from './components/entities/class/class-detail-v2/tabs/resources-tab/sub-tabs/cost-analysis/cost-analysis-sub-tab.component';
import { CostAnalysisTabComponent } from './components/entities/class/class-detail-v2/tabs/cost-analysis-tab/cost-analysis-tab.component';
import { ClassesV2Component } from './components/entities/class/classes-v2/classes-v2.component';
import { ClassCardComponent } from './components/entities/class/classes-v2/class-card/class-card.component';
import { VmLaunchModalComponent } from './components/shared/vm-launch-modal/vm-launch-modal.component';
import { ToastContainerComponent } from './components/shared/toast-container/toast-container.component';
import { RequisitionDetailViewComponent } from './components/entities/requisition/requisition-detail-view/requisition-detail-view.component';
import { PolicyCreatorComponent } from './components/entities/policy/policy-creator/policy-creator.component';
import { VmPolicyWizardComponent } from './components/entities/policy/vm-policy-wizard/vm-policy-wizard.component';
import { StepPolicyInfoComponent } from './components/entities/policy/vm-policy-wizard/steps/step-policy-info/step-policy-info.component';
import { StepResourceCreationComponent } from './components/entities/policy/vm-policy-wizard/steps/step-resource-creation/step-resource-creation.component';
import { StepVmCleanupComponent } from './components/entities/policy/vm-policy-wizard/steps/step-vm-cleanup/step-vm-cleanup.component';
import { UaPolicyWizardComponent } from './components/entities/policy/ua-policy-wizard/ua-policy-wizard.component';
import { StepUaResourceCreationComponent } from './components/entities/policy/ua-policy-wizard/steps/step-ua-resource-creation/step-ua-resource-creation.component';
import { StepUaCleanupComponent } from './components/entities/policy/ua-policy-wizard/steps/step-ua-cleanup/step-ua-cleanup.component';
import { StepUaQuotaLimitComponent } from './components/entities/policy/ua-policy-wizard/steps/step-ua-quota-limit/step-ua-quota-limit.component';
import { DuplicatePolicyDrawerComponent } from './components/entities/policy/duplicate-policy-drawer/duplicate-policy-drawer.component';
import { UaCredentialsCardComponent } from './components/resources/resource-cards/ua-credentials-card/ua-credentials-card.component';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { DownloadSnackbarComponent } from './components/test/vm-connection/download-snackbar-component';

export function initializeApp(configService: ConfigService, resourceMasterDataService: ResourceMasterDataService) {
  return async () => {
    Log.d('APP_INITIALIZER:Boot', 'Initializing application');

    // Make config service available globally for backward compatibility
    (window as any).configService = configService;
    Log.d('APP_INITIALIZER:Boot', "Made configService globally accessible on window object for backward compatibility");

    // Subscribe to config loaded event to trigger master data loading and other dependent initialization
    configService.configLoaded$.subscribe(async next => {
      if (next) {
        Log.d('APP_INITIALIZER:Init:Config', 'Config loaded', { apiUrl: configService.getApiUrl(), wsUrl: configService.getWsUrl() });
        resourceMasterDataService.serviceUrl = configService.getApiUrl();
        Log.d('APP_INITIALIZER:Init:Decorator', 'Decorator cache:', DecoratorFactory.getCachedRegistryNames());
        Log.d('APP_INITIALIZER:Init:Decorator', 'Registered components:', DecoratorFactory.getRegisteredComponentNames());
      }
    });

    // Wait for config to load
    await configService.initializeConfig();
  };
}

@NgModule({
  declarations: [
    AppComponent,
    WizardBuilderComponent,
    VirtualMachineComponent,
    CloudProviderPluginComponent,
    CloudProviderPreviewComponent,
    SoftwareConfigPluginComponent,
    SoftwarePreviewComponent,
    ResourceCardsComponent,
    LoginComponent,
    NotificationsComponent,
    NavHeaderComponent,
    MainLayoutComponent,
    SidebarComponent,
    NewCustomerDrawerComponent,
    ProfileComponent,
    SupportComponent,
    TrainerCreateEditComponent,
    CustomerCreateEditComponent,
    CourseListComponent,
    CourseDetailComponent,
    RequisitionComponent,
    RequisitionListComponent,
    RequisitionCreateWizardComponent,
    ClassDetailV2Component,
    DetailTabComponent,
    ActivityTabComponent,
    ParticipantDetailsTabComponent,
    SettingsTabComponent,
    TenantSettingsComponent,
    VmSubscriptionSettingsComponent,
    VmSubscriptionAddDialogComponent,
    AccountSubscriptionSettingsComponent,
    AccTemplateSettingsComponent,
    ScheduleSettingsComponent,
    TimePickerComponent,
    FacultySettingsComponent,
    ParticipantsSettingsComponent,
    ForwardSettingsComponent,
    VmTemplateSettingsComponent,
    ViewVmConnectionTemplateDetailsComponent,
    VmConnectionTemplateCreatePanelComponent,
    VmConnectionTemplateAttachComponent,
    VmConnectionTemplateListComponent,
    AdhocParticipantsDialogComponent,
    CustomerInvoiceComponent,
    DashboardComponent,
    ClassSessionComponent,
    TemplateDetailsComponent,
    TemplateDetailsPreviewComponent,
    TrimmerPipe,
    BillingDashboardComponent,
    RequisitionCreateComponent,
    RequisitionPluginComponent,
    RequisitionPreviewComponent,
    TrainingSchedulesPluginComponent,
    TrainingSchedulesPreviewComponent,
    AdditionalInformationPluginComponent,
    AdditionalInformationPreviewComponent,
    TrainerRequisitionViewComponent,
    RequisitionPreviewViewComponent,
    SignUpComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    CustomerEnquiryFormComponent,
    CustomerEnquiryListComponent,
    UserAccountPluginComponent,
    UserAccountComponent,
    ClassResourcesComponent,
    NoAccessComponent,
    UnauthorizedComponent,
    MyRequisitionComponent,
    UserCreationComponent,
    BusinessProcessComponent,
    TeamsComponent,
    ForwardRequisitionComponent,
    DeletionPolicyComponent,
    PolicyTemplateListComponent,
    ViewPolicyDetailsComponent,
    VMTemplateSubscriptionsComponent,
    TemplateSubBulkAddDialogComponent,
    DeleteConfirmDialogComponent,
    ResourcePolicyLinkingComponent,
    PolicyTemplateLinkingComponent,
    VmConnectionComponent,
    VmAadStatusCardComponent,
    RequisitionEditComponent,
    ConfirmDialogComponent,
    TypedConfirmDialogComponent,
    ResourcesTabComponent,
    UserVmsSubTabComponent,
    TemplatesSubTabComponent,
    UserAccountsSubTabComponent,
    CostAnalysisSubTabComponent,
    CostAnalysisTabComponent,
    ClassesV2Component,
    ClassCardComponent,
    VmLaunchModalComponent,
    ToastContainerComponent,
    ResourceCreatorV2Component,
    TemplateDetailsStepComponent,
    UserAccountCreateWizardComponent,
    VmCreateWizardComponent,
    RequisitionDetailViewComponent,
    PolicyCreatorComponent,
    VmPolicyWizardComponent,
    StepPolicyInfoComponent,
    StepResourceCreationComponent,
    StepVmCleanupComponent,
    UaPolicyWizardComponent,
    StepUaResourceCreationComponent,
    StepUaCleanupComponent,
    StepUaQuotaLimitComponent,
    DuplicatePolicyDrawerComponent,
    UaCredentialsCardComponent,
    TemplateInfoCardComponent,
    PersistPageSizeDirective,
    DownloadSnackbarComponent,
  ],
  imports: [
    DecoratorModule,
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    NgbTooltipModule,
    EntityServicesModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatMenuModule,
    MatBadgeModule,
    MatChipsModule,
    MatListModule,
    MatDividerModule,
    MatSelectModule,
    MatOptionModule,
    MatDialogModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatAutocompleteModule,
    MatButtonToggleModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatSlideToggleModule,
    MatProgressBarModule,
    MatRadioModule,
    MatTabsModule,
    MatExpansionModule,
    MatSidenavModule,
    MatStepperModule,
    MatToolbarModule,
    MatRipple,
    ClipboardModule,
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    // Keep-alive anchor: prevents esbuild from tree-shaking dynamically-loaded
    // components in production builds. Every class listed here MUST also be
    // decorated with @WizardPlugin or @CloudResource so that its decorator
    // side-effect populates DecoratorFactory.componentsByDecorator at import time.
    // When adding a new @WizardPlugin / @CloudResource component, add it here too.
    {
      provide: DYNAMIC_COMPONENTS_RETENTION,
      useValue: [
        // @WizardPlugin components
        TemplateDetailsComponent,
        CloudProviderPluginComponent,
        SoftwareConfigPluginComponent,
        UserAccountPluginComponent,
        RequisitionPluginComponent,
        TrainingSchedulesPluginComponent,
        AdditionalInformationPluginComponent,
        // @CloudResource components
        VirtualMachineComponent,
        RequisitionCreateComponent,
        UserAccountComponent,
      ]
    },
    DecoratorFactory.provider,
    AuthService,
    AuthGuard,

    LoginGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [ConfigService, ResourceMasterDataService],
      multi: true
    },
    provideAnimationsAsync()
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
